/**
 * Assistant panel (web) — clean Claude-style chat with model picker.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatMessage, ChatInput, SafeErrorNotice, safeErrorCode, errorToCode } from "@smart-shikka/react";
import type { Message } from "@smart-shikka/react";
import { createWorkspaceChatService } from "@smart-shikka/react";
import { useAuth } from "../auth.js";
import { useLocale } from "../ui.js";

type ChatProfile = "fast" | "reasoning" | "auto";

const PROFILE_OPTIONS: { id: ChatProfile; labelKey: "fast" | "reasoning" | "auto" }[] = [
  { id: "fast", labelKey: "fast" },
  { id: "reasoning", labelKey: "reasoning" },
  { id: "auto", labelKey: "auto" },
];

export function AssistantComposerPanel() {
  const { token, account } = useAuth();
  const { tr, locale } = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);

  const chatService = useMemo(
    () => (token ? createWorkspaceChatService({ getToken: () => token }) : undefined),
    [token],
  );

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [streaming, setStreaming] = useState(false);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ChatProfile>("auto");

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, preview]);

  const disabled = !chatService || account?.status !== "active";

  const handleSend = useCallback(
    async (content: string) => {
      if (!chatService || account?.status !== "active") return;
      setError(null);
      const userMessage: Message = {
        id: `user_${Date.now()}`,
        role: "user",
        content,
        timestamp: new Date(),
        status: "sent",
      };
      setMessages((prev) => [...prev, userMessage]);
      setStreaming(true);
      setPreview("");

      try {
        const streamInput: {
          conversationId: string | undefined;
          message: string;
          logicalProfile?: "fast" | "reasoning";
        } = {
          conversationId,
          message: content,
        };
        if (profile !== "auto") streamInput.logicalProfile = profile;
        await chatService.stream(streamInput, {
            onChunk: (chunk) => setPreview((prev) => prev + chunk),
            onConversationCreated: (id) => setConversationId(id),
            onDone: (final) => {
              setMessages((prev) => [...prev, final]);
              setPreview("");
              setStreaming(false);
              if (final.status === "error") {
                // Never render the raw message — only an identifier-shaped code.
                setError(safeErrorCode(final.errorMessage));
              }
            },
          },
        );
      } catch (err) {
        setError(errorToCode(err) === "generic_error" ? "chat_send_failed" : errorToCode(err));
        setStreaming(false);
        setPreview("");
      }
    },
    [chatService, conversationId, profile, tr, account?.status],
  );

  const handleStop = useCallback(() => {
    chatService?.stop?.();
    setStreaming(false);
  }, [chatService]);

  const placeholder = disabled
    ? tr("chatUnavailable")
    : `${tr("composerPlaceholder")} (${locale === "hi" ? "Enter भेजें" : "Enter to send"})`;

  return (
    <section className="panel-card assistant-panel assistant-panel-full">
      <div className="assistant-layout">
        <div className="assistant-chat">
          <div className="assistant-header">
            <h3>{tr("assistantTitle")}</h3>
            <p className="section-desc">{tr("assistantDesc")}</p>
            <div className="assistant-controls">
              <label htmlFor="profile-picker">{locale === "hi" ? "मॉडल:" : "Model:"}</label>
              <select
                id="profile-picker"
                value={profile}
                disabled={disabled || streaming}
                onChange={(e) => setProfile(e.target.value as ChatProfile)}
              >
                {PROFILE_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {tr(p.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            {error && (
              <SafeErrorNotice code={error} locale={locale === "hi" ? "hi" : "en"} />
            )}
          </div>

          <div className="assistant-messages" ref={scrollRef} role="log" aria-live="polite">
            {messages.length === 0 && !streaming && !preview ? (
              <div className="assistant-empty">
                <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
                <h4>{locale === "hi" ? "स्मार्ट शिक्षा AI" : "Smart Shikka AI"}</h4>
                <p className="muted">{locale === "hi" ? "मुझसे कुछ भी पूछें" : "Ask me anything"}</p>
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isLastAssistant={message.role === "assistant" && index === messages.length - 1}
                  />
                ))}
                {streaming && preview && (
                  <ChatMessage
                    message={{
                      id: "streaming-preview",
                      role: "assistant",
                      content: preview,
                      timestamp: new Date(),
                      status: "streaming",
                    }}
                    isStreaming
                  />
                )}
              </>
            )}
          </div>

          <div className="assistant-composer">
            <ChatInput
              onSend={handleSend}
              disabled={disabled}
              placeholder={placeholder}
              isStreaming={streaming}
              onStopStreaming={handleStop}
              onVoiceTranscript={() => {}}
              ariaLabel={tr("composerPlaceholder")}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
